import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ElliottWorking from '../../../../../components/ui/ElliottWorking';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';
import { useAIMDContext } from '../context/AIMDContext';
import ManualEditModal from '../components/ManualEditModal';
import ImproveWithAIModal from '../components/ImproveWithAIModal';

const PreviewStep: React.FC = () => {
  const ctx = useAIMDContext();
  const { state, generationPhase, generationError, handleGeneratePreview, handleImprovePreview, updatePreview } = ctx;
  const preview = state.preview.generatedPreview;
  const isLoading = generationPhase === 'preview';

  const [manualEditOpen, setManualEditOpen] = useState(false);
  const [improveOpen, setImproveOpen] = useState(false);

  const hasTriggered = useRef(false);

  useEffect(() => {
    if (!preview && !isLoading && !hasTriggered.current) {
      hasTriggered.current = true;
      handleGeneratePreview();
    }
  }, [preview, isLoading, handleGeneratePreview]);

  const handleManualSave = useCallback((text: string) => {
    updatePreview({ generatedPreview: text });
    setManualEditOpen(false);
  }, [updatePreview]);

  const handleImproveSubmit = useCallback(async (feedback: string) => {
    await handleImprovePreview(feedback);
    setImproveOpen(false);
  }, [handleImprovePreview]);

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', maxWidth: 720, display: 'flex', flexDirection: 'column' }}>
        <ElliottWorking size="full" message="Elliott is building your program preview..." />
      </Box>
    );
  }

  if (generationError && !preview) {
    return (
      <Box sx={{ width: '100%', maxWidth: 720, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
        <Typography sx={{ fontSize: 16, color: 'var(--UI-Error, #F44336)', fontFamily: 'Inter' }}>
          {generationError}
        </Typography>
        <DosenButton
          tone="cta"
          sizeVariant="lg"
          onClick={() => {
            hasTriggered.current = false;
            handleGeneratePreview();
          }}
        >
          Try Again
        </DosenButton>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography sx={{ fontSize: 30, fontWeight: 500, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}>
          Program Preview
        </Typography>
        <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', mt: 1, lineHeight: '20px' }}>
          Review your program preview before we proceed to build your program structure.{'\n'}
          Updating your previous inputs will update your preview.
        </Typography>
      </Box>

      {/* Preview content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Stroke-highlight, #F79420)' }}>
          {state.requirements.programName}
        </Typography>
        {preview && preview.split('\n\n').map((paragraph, i) => (
          <Typography
            key={i}
            sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '22px' }}
          >
            {paragraph}
          </Typography>
        ))}
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <Box
          component="button"
          onClick={() => setManualEditOpen(true)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, background: 'transparent',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)', borderRadius: '8px',
            px: 2, py: 1.5, cursor: 'pointer', color: 'var(--Text-text-primary, #FDFDFD)',
            fontFamily: 'Inter', fontSize: 13, fontWeight: 500,
            '&:hover': { borderColor: 'var(--Stroke-highlight, #F79420)' }
          }}
        >
          Improve Manually
        </Box>
        <Box
          component="button"
          onClick={() => setImproveOpen(true)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, background: 'transparent',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)', borderRadius: '8px',
            px: 2, py: 1.5, cursor: 'pointer', color: 'var(--Text-text-primary, #FDFDFD)',
            fontFamily: 'Inter', fontSize: 13, fontWeight: 500,
            '&:hover': { borderColor: 'var(--Stroke-highlight, #F79420)' }
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 16, color: 'var(--Stroke-highlight, #F79420)' }} />
          Improve with Elliott
        </Box>
      </Box>

      <ManualEditModal
        open={manualEditOpen}
        currentText={preview || ''}
        onClose={() => setManualEditOpen(false)}
        onSave={handleManualSave}
      />

      <ImproveWithAIModal
        variant="preview"
        open={improveOpen}
        onClose={() => setImproveOpen(false)}
        onSubmit={handleImproveSubmit}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default PreviewStep;
